import decode from 'jwt-decode'

/**
 * Token storage.
 *
 * The Cognito id token is stored here and read back on later loads. The keys
 * match the dashboard's — same origin, so the two apps share one signed-in
 * session.
 */

export const getTokenExpirationDate = token => {
  let decoded
  try {
    decoded = decode(token)
  } catch (error) {
    // A token we cannot read is not one we can trust an expiry from.
    return null
  }

  if (!decoded.exp) {
    return null
  }

  const date = new Date(0) // The 0 here is the key, which sets the date to the epoch
  date.setUTCSeconds(decoded.exp)
  return date
}

// How long before expiry a token counts as due for renewal. The Cognito id
// token lives 15 minutes, so the lead has to sit comfortably inside that: long
// enough that a renewal has time to land, short enough that most of the token's
// life is still used.
const RENEW_LEAD_MS = 1000 * 60 * 5

export const isTokenCloseToExpiration = token => {
  const tokenToCheck = token || getToken()
  if (!tokenToCheck) {
    throw new Error('No token provided to check expiration')
  }
  const date = getTokenExpirationDate(tokenToCheck)
  if (date === null) {
    return false
  }

  return (date.valueOf() - RENEW_LEAD_MS) <= new Date().valueOf()
}

export const isTokenExpired = token => {
  const tokenToCheck = token || getToken()
  if (!tokenToCheck) {
    throw new Error('No token provided to check expiration')
  }
  const date = getTokenExpirationDate(tokenToCheck)
  const offsetSeconds = 0
  if (date === null) {
    // Fail closed: a token whose expiry we cannot read (undecodable, or no
    // `exp` claim) must count as expired. Returning false here left a corrupt
    // stored id_token counting as valid forever, so isLoggedIn() stayed true
    // with no way to recover. Treating it as expired makes isLoggedIn() clear
    // the token and force a fresh sign-in.
    return true
  }

  return !(date.valueOf() > (new Date().valueOf() + (offsetSeconds * 1000)))
}

export const isLoggedIn = () => {
  // Checks if there is a saved token and it's still valid
  const token = getToken(),
        isLoggedIn = !!token && !isTokenExpired(token)

  if (!isLoggedIn) {
    clearTokenAndProfile()
  }

  return isLoggedIn
}

export const setToken = token => {
  // Saves user token to localStorage
  if (typeof Storage !== 'undefined' && token) {
    localStorage.setItem('id_token', token)
  }
}

export const clearToken = () => {
  // Saves user token to localStorage
  if (typeof Storage !== 'undefined') {
    localStorage.removeItem('id_token')
  }
}

export const clearTokenAndProfile = () => {
  if (typeof Storage !== 'undefined') {
    localStorage.removeItem('id_token')
    localStorage.removeItem('profile')
    localStorage.removeItem('organisationId')
  }
}

export const getToken = () => {
  // Retrieves the user token from localStorage
  if (typeof Storage !== 'undefined') {
    return localStorage.getItem('id_token')
  }
  return null
}
